import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Unlink,
  Image as ImageIcon,
  Table as TableIcon,
  Undo,
  Redo,
  Code,
  Eye,
  Edit3,
} from 'lucide-react';
import './TiptapEditor.css';

export default function TiptapEditor({
  content = '',
  onChange,
  placeholder = 'Rédigez votre contenu ici...',
  readOnly = false,
}) {
  const [isPreview, setIsPreview] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: false, // Strict backend rule: disallow base64
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: content || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  if (!editor) {
    return <div className="tiptap-loading">Initialisation de l'éditeur...</div>;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt("URL du lien (ex: https://...):", previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Basic protocol check
    const validUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: validUrl }).run();
  };

  const addImage = () => {
    const url = window.prompt("URL de l'image (HTTPS recommandé):");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  return (
    <div className={`tiptap-container ${readOnly ? 'tiptap-readonly' : ''}`}>
      {/* Toolbar */}
      {!readOnly && (
        <div className="tiptap-toolbar">
          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('bold') ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Gras (Ctrl+B)"
            >
              <Bold size={16} />
            </button>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('italic') ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Italique (Ctrl+I)"
            >
              <Italic size={16} />
            </button>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('underline') ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              title="Souligné (Ctrl+U)"
            >
              <UnderlineIcon size={16} />
            </button>
          </div>

          <div className="tiptap-toolbar-divider" />

          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              title="Titre H1"
            >
              <Heading1 size={16} />
            </button>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              title="Titre H2"
            >
              <Heading2 size={16} />
            </button>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              title="Titre H3"
            >
              <Heading3 size={16} />
            </button>
          </div>

          <div className="tiptap-toolbar-divider" />

          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              title="Liste à puces"
            >
              <List size={16} />
            </button>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              title="Liste numérotée"
            >
              <ListOrdered size={16} />
            </button>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              title="Citation"
            >
              <Quote size={16} />
            </button>
          </div>

          <div className="tiptap-toolbar-divider" />

          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('link') ? 'active' : ''}`}
              onClick={setLink}
              title="Insérer un lien"
            >
              <LinkIcon size={16} />
            </button>
            {editor.isActive('link') && (
              <button
                type="button"
                className="tiptap-btn"
                onClick={() => editor.chain().focus().unsetLink().run()}
                title="Supprimer le lien"
              >
                <Unlink size={16} />
              </button>
            )}
            <button
              type="button"
              className="tiptap-btn"
              onClick={addImage}
              title="Insérer une image par URL"
            >
              <ImageIcon size={16} />
            </button>
            <button
              type="button"
              className={`tiptap-btn ${editor.isActive('table') ? 'active' : ''}`}
              onClick={insertTable}
              title="Insérer un tableau (3x3)"
            >
              <TableIcon size={16} />
            </button>
          </div>

          <div className="tiptap-toolbar-divider" />

          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className="tiptap-btn"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Annuler"
            >
              <Undo size={16} />
            </button>
            <button
              type="button"
              className="tiptap-btn"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Rétablir"
            >
              <Redo size={16} />
            </button>
          </div>

          <div className="tiptap-toolbar-spacer" />

          <div className="tiptap-toolbar-group">
            <button
              type="button"
              className={`tiptap-preview-toggle ${isPreview ? 'active' : ''}`}
              onClick={() => setIsPreview(!isPreview)}
            >
              {isPreview ? <Edit3 size={15} /> : <Eye size={15} />}
              <span>{isPreview ? 'Éditer' : 'Aperçu HTML'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Content Area or Preview */}
      {isPreview ? (
        <div
          className="tiptap-preview-body"
          dangerouslySetInnerHTML={{ __html: editor.getHTML() || '<p><em>Aucun contenu</em></p>' }}
        />
      ) : (
        <EditorContent editor={editor} className="tiptap-content-editable" />
      )}
    </div>
  );
}
